import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios'
import { 
  HumanizeRequest, 
  HumanizeResponse, 
  SampleTransformation, 
  Style,
  HistoryItem,
  HistoryRequest,
  UsageStats,
  ApiError 
} from '../types'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const apiError: ApiError = {
      message: 'An error occurred',
      code: 'UNKNOWN_ERROR',
      status: 500,
    }

    if (error.response) {
      apiError.status = error.response.status
      apiError.message = (error.response.data as { message?: string })?.message || error.message
      apiError.code = (error.response.data as { code?: string })?.code || `ERR_${error.response.status}`
    } else if (error.request) {
      apiError.message = 'No response from server. Please check your connection.'
      apiError.code = 'NETWORK_ERROR'
    }

    return Promise.reject(apiError)
  }
)

// Humanize API
export const humanizeApi = {
  transform: async (data: HumanizeRequest): Promise<HumanizeResponse> => {
    const response = await apiClient.post('/humanize/transform', {
      text: data.text,
      style: data.style,
      preserve_structure: data.options?.preserveFormatting ?? true,
    })
    const result = response.data
    return {
      original: result.original_text,
      humanized: result.humanized_text,
      style: result.style,
      processingTime: 0,
      tokensChanged: Math.abs((result.word_count?.humanized ?? 0) - (result.word_count?.original ?? 0)),
      confidenceScore: Math.round((result.confidence_score ?? 0) * 100),
    }
  },
  
  getSample: async (style: string): Promise<SampleTransformation> => {
    const response = await apiClient.get(`/styles/preview/${style}`)
    const result = response.data
    return {
      original: result.example_input,
      transformed: result.example_output,
      style: result.style_id,
    }
  },
}

// Styles API
export const stylesApi = {
  getAll: async (): Promise<Style[]> => {
    const response = await apiClient.get('/styles')
    return (response.data.styles || []).map((style: any) => ({
      id: style.id,
      name: style.name_en || style.name,
      description: style.description,
      icon: style.emoji,
    }))
  },
}

// History API
export const historyApi = {
  getAll: async (): Promise<HistoryItem[]> => {
    const response = await apiClient.get('/history')
    return (response.data.items || []).map((item: any) => ({
      id: item.id,
      original: item.original_preview || '',
      humanized: item.humanized_preview || '',
      style: item.style,
      createdAt: item.created_at,
    }))
  },
  
  create: async (data: HistoryRequest): Promise<HistoryItem> => {
    const response = await apiClient.post<HistoryItem>('/history', data)
    return response.data
  },
  
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/history/${id}`)
  },
  
  toggleFavorite: async (id: string, favorite: boolean): Promise<HistoryItem> => {
    const response = await apiClient.patch<HistoryItem>(`/history/${id}`, { favorite })
    return response.data
  },
}

// Statistics API
export const statsApi = {
  getUsageStats: async (): Promise<UsageStats> => {
    const response = await apiClient.get<UsageStats>('/stats')
    return response.data
  },
}

export default apiClient
